/**
 * Page Tracker
 * Tracks time spent on pages and tab visibility
 */

import { sendNewRelicEvent } from '../newrelic';

import { ANALYTICS_EVENTS, PAGE_TO_FEATURE, IDLE_TIMEOUT_MS } from './constants';

// Internal state
let currentPage = null;
let pageEntryTime = null;
let lastActivityTime = null;
let isTabVisible = true;
let idleTimer = null;
let isIdle = false;
let activityDebounceTimer = null;
const ACTIVITY_DEBOUNCE_MS = 2000; // Debounce activity events by 2 seconds

/**
 * Get feature name from path
 */
const getFeatureFromPath = (path) => {
  // Check for exact match first
  if (PAGE_TO_FEATURE[path]) {
    return PAGE_TO_FEATURE[path];
  }

  // Check for partial matches (for paths with params)
  const matchingKey = Object.keys(PAGE_TO_FEATURE).find((key) => path.startsWith(key));
  return matchingKey ? PAGE_TO_FEATURE[matchingKey] : null;
};

/**
 * Calculate time spent in seconds
 */
const calculateTimeSpent = () => {
  if (!pageEntryTime) return 0;
  return Math.round((Date.now() - pageEntryTime) / 1000);
};

/**
 * Track page exit
 * @param {string} exitReason - Why the user left (navigation, tab-close, timeout)
 */
const trackPageExit = (exitReason = 'navigation') => {
  if (!currentPage || !pageEntryTime) return;

  const timeSpentSeconds = calculateTimeSpent();
  const feature = getFeatureFromPath(currentPage);

  sendNewRelicEvent(ANALYTICS_EVENTS.PAGE_EXITED, {
    exitReason,
    exitTimestamp: new Date().toISOString(),
    featureName: feature,
    pageName: currentPage,
    timeSpentSeconds,
  });

  // Clear state
  if (idleTimer) {
    clearTimeout(idleTimer);
  }
};

/**
 * Reset idle timer
 */
const resetIdleTimer = () => {
  if (idleTimer) {
    clearTimeout(idleTimer);
  }

  if (isIdle) {
    isIdle = false;
    sendNewRelicEvent(ANALYTICS_EVENTS.USER_ACTIVE, {
      idleDurationSeconds: Math.round((Date.now() - lastActivityTime) / 1000),
      pageName: currentPage,
    });
  }

  lastActivityTime = Date.now();

  idleTimer = setTimeout(() => {
    isIdle = true;
    sendNewRelicEvent(ANALYTICS_EVENTS.USER_IDLE, {
      pageName: currentPage,
    });
  }, IDLE_TIMEOUT_MS);
};

/**
 * Handle user activity (mouse, keyboard, scroll)
 * Debounced to prevent excessive event firing
 */
const handleUserActivity = () => {
  if (!isTabVisible) return;

  // Debounce activity handling to reduce event frequency
  if (activityDebounceTimer) {
    clearTimeout(activityDebounceTimer);
  }

  activityDebounceTimer = setTimeout(() => {
    resetIdleTimer();
    activityDebounceTimer = null;
  }, ACTIVITY_DEBOUNCE_MS);
};

/**
 * Handle tab visibility change
 */
const handleVisibilityChange = () => {
  const wasVisible = isTabVisible;
  isTabVisible = !document.hidden;

  sendNewRelicEvent(ANALYTICS_EVENTS.TAB_VISIBILITY_CHANGED, {
    hiddenDurationSeconds: wasVisible && !isTabVisible ? 0 : calculateTimeSpent(),
    pageName: currentPage,
    visible: isTabVisible,
  });

  if (isTabVisible) {
    resetIdleTimer();
  } else if (idleTimer) {
    clearTimeout(idleTimer);
  }
};

/**
 * Initialize activity listeners
 */
const initActivityListeners = () => {
  if (typeof window === 'undefined') return;

  // Activity events
  window.addEventListener('mousemove', handleUserActivity, { passive: true });
  window.addEventListener('keydown', handleUserActivity, { passive: true });
  window.addEventListener('scroll', handleUserActivity, { passive: true });
  window.addEventListener('click', handleUserActivity, { passive: true });

  // Visibility change
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Before unload - send final page exit
  window.addEventListener('beforeunload', () => {
    if (currentPage) {
      trackPageExit('tab-close');
    }
  });
};

/**
 * Remove activity listeners
 */
const removeActivityListeners = () => {
  if (typeof window === 'undefined') return;

  window.removeEventListener('mousemove', handleUserActivity);
  window.removeEventListener('keydown', handleUserActivity);
  window.removeEventListener('scroll', handleUserActivity);
  window.removeEventListener('click', handleUserActivity);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * Track page entry
 * @param {string} path - The page path
 * @param {string} referrer - The previous page (optional)
 */
export const trackPageEntry = (path, referrer = null) => {
  // Exit previous page first
  if (currentPage && currentPage !== path) {
    trackPageExit('navigation');
  }

  currentPage = path;
  pageEntryTime = Date.now();
  lastActivityTime = Date.now();
  isIdle = false;

  const feature = getFeatureFromPath(path);

  sendNewRelicEvent(ANALYTICS_EVENTS.PAGE_ENTERED, {
    entryTimestamp: new Date().toISOString(),
    featureName: feature,
    pageName: path,
    referrer: referrer || document.referrer,
  });

  resetIdleTimer();
};

// Export trackPageExit for external use
export { trackPageExit };

/**
 * Get current page info
 */
export const getCurrentPageInfo = () => ({
  currentPage,
  isIdle,
  isTabVisible,
  timeSpentSeconds: calculateTimeSpent(),
});

/**
 * Initialize page tracker
 */
export const initPageTracker = () => {
  initActivityListeners();
};

/**
 * Cleanup page tracker
 */
export const cleanupPageTracker = () => {
  if (currentPage) {
    trackPageExit('cleanup');
  }
  removeActivityListeners();
  if (idleTimer) {
    clearTimeout(idleTimer);
  }
  if (activityDebounceTimer) {
    clearTimeout(activityDebounceTimer);
  }
};

export default {
  cleanupPageTracker,
  getCurrentPageInfo,
  initPageTracker,
  trackPageEntry,
  trackPageExit,
};
